import {
  Button,
  Link,
  Skeleton,
  SkeletonItem,
} from "@fluentui/react-components";
import { AddRegular } from "@fluentui/react-icons";
import { ITeamRole } from "models/IProjectIQWorkspace";
import { useState } from "react";
import { AddRoleDialog } from "./AddRoleDialog";
import { TeamRoleCard } from "./TeamRoleCard";
import { useTeamRoleCardStyles } from "./TeamRoleCard.styles";
import { useTeamRolesSectionStyles } from "./TeamRolesSection.styles";
import { ITeamRolesSectionProps } from "./TeamRolesSection.types";

export const TeamRolesSection = ({
  teamRoles,
  members,
  canEdit,
  onRolesChange,
  peopleLinks,
  isLinksLoading,
}: ITeamRolesSectionProps): JSX.Element => {
  const styles = useTeamRolesSectionStyles();
  const cardStyles = useTeamRoleCardStyles();
  const [isAddRoleDialogOpen, setIsAddRoleDialogOpen] = useState(false);

  const handleRoleChange = (updatedRole: ITeamRole) => {
    onRolesChange(
      teamRoles.map((r) => (r.id === updatedRole.id ? updatedRole : r)),
    );
  };

  const handleDeleteRole = (roleIdentifier: string) => {
    onRolesChange(teamRoles.filter((r) => r.id !== roleIdentifier));
  };

  const handleAddRole = (newRole: ITeamRole) => {
    onRolesChange([...teamRoles, newRole]);
    setIsAddRoleDialogOpen(false);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.headingGroup}>
          <h2 className={styles.heading}>Team Roles</h2>
          {isLinksLoading ? (
            <Skeleton>
              <div className={styles.skeletonLinksContainer}>
                <SkeletonItem
                  animation="wave"
                  className={styles.skeletonLink}
                  style={{ width: "52%" }}
                />
                <SkeletonItem
                  animation="wave"
                  className={styles.skeletonLink}
                  style={{ width: "38%" }}
                />
              </div>
            </Skeleton>
          ) : (
            peopleLinks.map((link) => (
              <Link
                key={link.id}
                href={link.url}
                target="_blank"
                className={styles.learnMoreLink}
              >
                {link.displayName} →
              </Link>
            ))
          )}
        </div>
        <div className={styles.headerActions}>
          <Button
            appearance="primary"
            icon={<AddRegular aria-hidden="true" />}
            onClick={() => setIsAddRoleDialogOpen(true)}
            aria-label="Add team member role"
            disabled={!canEdit}
          >
            Add team member role
          </Button>
        </div>
      </div>

      <div className={styles.cardGrid}>
        {teamRoles.map((role) => (
          <TeamRoleCard
            key={role.id}
            role={role}
            members={members}
            canEdit={canEdit}
            onRoleChange={handleRoleChange}
            onDelete={handleDeleteRole}
          />
        ))}
      </div>

      {canEdit && (
        <button
          className={cardStyles.addResponsibilityButton}
          onClick={() => setIsAddRoleDialogOpen(true)}
          aria-label="Add team member role"
        >
          <AddRegular aria-hidden="true" />
          Add team member role
        </button>
      )}

      <AddRoleDialog
        isOpen={isAddRoleDialogOpen}
        members={members}
        onDismiss={() => setIsAddRoleDialogOpen(false)}
        onConfirm={handleAddRole}
      />
    </div>
  );
};
